// Third-party
import React, { Component } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import classNames from 'classnames';
import round from 'lodash/round';

// Components
import ChartLegend from './ChartLegend';

// Utils
import { formatMessage } from '../../../utils/i18n';
import colorUtils from '../../../utils/colors';
import { ChartPattern } from '../../../utils/patterns';
import HoldingTypeAllocation from '@/types/holding-type-allocation';
import Nullable from '@/types/nullable';

type Props = {
  data: HoldingTypeAllocation;
  dark?: boolean;
} & Partial<DefaultProps>;

type DefaultProps = {
  animate: boolean;
  height: Nullable<number>;
  tooltip: boolean;
  color: string;
  withLegend: boolean;
};

type State = {
  chartOptions: Highcharts.Options;
};

// eslint-disable-next-line
const tooltipFormatter: Highcharts.TooltipFormatterCallbackFunction = function () {
  return `${this.point.name} ${this.y}%`;
};

class SunburstChart extends Component<Props, State> {
  static defaultProps: DefaultProps = {
    animate: true,
    height: null,
    tooltip: true,
    color: '#2d8eff',
    withLegend: true,
  };

  readonly state: State = {
    chartOptions: {
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        height: this.props.height,
      },
      plotOptions: {
        pie: {
          shadow: false,
          center: ['50%', '50%'],
          states: {
            inactive: {
              opacity: 1,
            },
          },
        },
      },
      title: undefined,
      credits: { enabled: false },
      tooltip: {
        enabled: this.props.tooltip,
        backgroundColor: '#000000',
        borderColor: '#000000',
        borderRadius: 6,
        shadow: false,
        style: {
          color: '#ffffff',
        },
        formatter: tooltipFormatter,
      },
    },
  };

  componentDidMount() {
    this.updateChart();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.data !== this.props.data) {
      this.updateChart();
    }
  }

  getSeriesData() {
    const { data, color } = this.props;

    type PieChartDatum = {
      name: string;
      y: number;
      color: string | ChartPattern;
    };

    // TODO -- why the "| undefined"?
    const colorDictionary = colorUtils.getSunburstDictionary(color || SunburstChart.defaultProps.color);

    const holdingTypeData: PieChartDatum[] = [];
    let weightData: PieChartDatum[] = [];

    for (const [key, value] of Object.entries(data)) {
      const weightColors = colorDictionary[key].weight;

      holdingTypeData.push({
        name: formatMessage(`Charts.Sunburst.Allocations.${key}`),
        y: round(value.total, 2),
        color: colorDictionary[key].fundType,
      });

      weightData = weightData.concat(
        value.holdings.map((holding, i) => ({
          name: holding.symbol || '-',
          y: round(holding.weight, 2),
          color: weightColors[i % weightColors.length],
        })),
      );
    }

    return [
      {
        name: formatMessage('Charts.Sunburst.AssetType'),
        data: holdingTypeData,
        size: '100%',
        label: { enabled: false },
        states: {
          hover: {
            brightness: 0.1,
            halo: {
              size: 0,
            },
          },
        },
        dataLabels: {
          enabled: false,
        },
      },
      {
        name: formatMessage('Charts.Sunburst.Fund'),
        data: weightData,
        size: '100%',
        label: { enabled: false },
        innerSize: '60%',
        dataLabels: {
          enabled: false,
        },
        states: {
          hover: {
            brightness: 0.1,
            halo: {
              size: 5,
            },
          },
        },
      },
    ];
  }

  updateChart() {
    const series = this.getSeriesData();
    this.setState({ chartOptions: { series } as any });
  }

  render() {
    const { dark, withLegend } = this.props;
    const { chartOptions } = this.state;

    return (
      <div id="chart" className={classNames('sunburst-chart', { dark })}>
        <div className="flex-grow-0">
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>

        {withLegend && <ChartLegend className="sunburst-legend" chartOptions={chartOptions} />}
      </div>
    );
  }
}

export default SunburstChart;
